import axios, { CancelToken } from 'axios';
import store from '@/store';
import Vue from 'vue';
import User from '@/services/User/User';
import renewToken from './renewToken';

const EXPIRED_MESSAGE = 'EXPIRED_MESSAGE';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  headers: {
    Authorization: 'Bearer {token}',
  },
});

api.CancelToken = axios.CancelToken;
api.isCancel = axios.isCancel;

// Set the AUTH token for any request
api.interceptors.request.use((config) => {
  const token = store.getters.authToken;
  config.headers.Authorization = token ? `Bearer ${token}` : '';

  return config;
});

// On a 401, silently renew the access token from the still-valid Auth0 session
// and retry the request once. Only log out (destroying the SSO session) if that
// renewal fails, i.e. the session is genuinely gone.
api.interceptors.response.use(
  response => Promise.resolve(response),
  async (error) => {
    // We are checking here to see if they are expired, and to log them out if so
    if (error.message === EXPIRED_MESSAGE) {
      store.dispatch('logout', { expired: true });
      return Promise.reject(error);
    }

    const original = error.config;

    if (error.response && error.response.status === 401 && original && !original._retry) {
      original._retry = true;

      try {
        await renewToken();
      } catch (renewalError) {
        // Renewal failed. For an MFA-enforced user the silent-auth iframe can
        // NEVER succeed (an iframe cannot complete an MFA challenge), so
        // redirecting to login bounces off the still-alive SSO session, returns
        // silently, and 401s again — an infinite reload loop (CS-1929). Log out
        // instead: this destroys the SSO session so the next load lands on the
        // interactive login (which CAN do MFA) and the loop terminates.
        // .catch contains a logout failure so it cannot escape as an unhandled
        // rejection; logout's finally has already cleared local auth state.
        store.dispatch('logout', { expired: true }).catch(() => {});
        return Promise.reject(error);
      }

      // Retry once with the freshly saved token (the request interceptor
      // injects it). A rejection here is a real request failure and must
      // propagate normally, NOT be misread as a dead session.
      return api(original);
    }

    return Promise.reject(error);
  },
);

export default api;
